import {AbstractO2dElement} from "@o2d/shared";
import {html, nothing, PropertyValues} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {until} from "lit/directives/until.js";
import {guard} from "lit/directives/guard.js";
import {ClusterDetector, type CrosspointDataProvider, type Viewport, type ViewportItem} from '@o2d/datasource';
import type {AreaConfiguration} from "../areas/area-configuration.interface";
import type {ColumnUnderlayRenderer} from "../types/column-underlay-renderer.type";
import type {RowUnderlayRenderer} from "../types/row-underlay-renderer.type";
import {repeat} from "lit/directives/repeat.js";
import {StyleInfo, styleMap} from "lit/directives/style-map.js";
import {classMap} from "lit/directives/class-map.js";
import { map } from "lit/directives/map.js";
import { range } from "lit/directives/range.js";
import {CellClickedEventDetail} from "../types/cell-clicked-event-detail.interface";
import {CellEventDetail} from "../types/cell-event-detail.interface";
import {findTargetItemId} from "../utils/find-target-item-id";
import {AreaCellTrackingController} from "../controllers/area-cell-tracking.controller";
import {fromEvent, map as rxMap, filter, distinctUntilChanged, share, Observable, merge, startWith, withLatestFrom} from "rxjs";
import {AreaCellPosition} from "../interfaces/area-cell-position.interface";
import {AreaCellPositionCache} from "../controllers/area-cell-position-cache";

/** How long after a touch a mouse event still counts as emulated from it. */
const TOUCH_EMULATION_WINDOW_MS = 1000;

/**
 * True when a mouse event was emulated by the browser from a touch. Where the browser says so
 * itself (`sourceCapabilities`, Chromium) that answer is final, so a real mouse on a touch laptop
 * is never ignored; elsewhere (Safari, Firefox) a mouse event right after a touch counts as
 * emulated.
 */
export function isEmulatedFromTouch(event: MouseEvent, lastTouchAt: number): boolean {
    const capabilities = (event as MouseEvent & { sourceCapabilities?: { firesTouchEvents?: boolean } | null }).sourceCapabilities;
    if (capabilities) {
        return capabilities.firesTouchEvents === true;
    }
    return performance.now() - lastTouchAt < TOUCH_EMULATION_WINDOW_MS;
}

/**
 * Represents a grid area element, a custom web component responsible for rendering
 * a discrete portion of a grid using data provided by a `DataProvider`. This element
 * dynamically calculates and renders grid items based on its configuration, spatial
 * dimensions, and cursor position.
 *
 * Note: TData parameter was removed to support heterogeneous item types across areas.
 * Type narrowing should be done in cellRenderer implementations.
 *
 * ## Events
 *
 * - `cell-clicked` - Dispatched when a grid cell is clicked (left-click only).
 *   Event detail contains axis coordinates, items at the clicked crosspoint,
 *   and keyboard modifier state. See {@link CellClickedEventDetail} for details.
 *
 * - `cell-hover-changed` - Dispatched when mouse enters or leaves a grid cell (opt-in via `enableCellHoverEvent`).
 *   Event detail contains axis coordinates, items at the crosspoint, and state ('enter' | 'leave').
 *   Uses cell-based tracking (PRD-020) with 120ms debounce for enter events and immediate leave events.
 *
 * ## Event Architecture (PRD-020)
 *
 * The event system uses a 2-layer RxJS-based architecture:
 * - **Layer 1 (Foundation):** `AreaCellPositionCache` converts pixel coordinates to grid cells,
 *   `areaCellPosition$` Observable streams cell positions on mousemove with `distinctUntilChanged`
 *   deduplication per cell index pair.
 * - **Layer 2 (Consumer):** `AreaCellTrackingController` subscribes to the observable, debounces
 *   enter events (120ms), and uses `switchMap` for automatic cancellation on rapid cell switches.
 *
 * Key differences from previous item-based system:
 * - **Cell-based deduplication**: Events dispatch per cell, not per item (multi-cell items dispatch multiple events)
 * - **Empty cell support**: Events dispatch even when `items.length === 0`
 * - **Leave on cell switch**: Every enter has a corresponding leave (via `pairwise()`)
 * - **Cache invalidation**: Automatically invalidated on scroll (areaCursorIndexX/Y changes) and resize (ResizeObserver)
 *
 * @template TXValue Type of the TXAxisValue-axis values in the grid.
 * @template TYValue Type of the TYAxisValue-axis values in the grid.
 */
@customElement(`o2d-area`)
export class O2dAreaElement<TXValue = unknown, TYValue = unknown> extends AbstractO2dElement {

    @property()
    areaConfiguration!: AreaConfiguration<TXValue, TYValue>;

    @property({type: Number})
    areaSpanX!: number;

    @property({type: Number})
    areaSpanY!: number;

    @property()
    areaCursorIndexX!: number;

    @property()
    areaCursorIndexY!: number;

    @property({type: Object})
    dataProvider!: CrosspointDataProvider<TXValue, TYValue, unknown>;

    /**
     * Monotonically increasing version counter for the data provider's state.
     * Triggers re-render when the provider's underlying data changes
     * without changing the provider reference itself.
     */
    @property({type: Number})
    dataProviderVersion = 0;

    /** Tracks previously applied CSS classes for cleanup on config change. */
    private appliedClasses: string[] = [];

    /**
     * Hover detection controller with 120ms delay
     * @internal Registered automatically via ReactiveController pattern
     */
    // @ts-expect-error - ReactiveController auto-registers via constructor, field value never accessed directly
    private readonly _hoverController = new AreaCellTrackingController(this as O2dAreaElement);

    /** Bound click handler for cleanup on disconnect */
    private boundClickHandler: ((event: MouseEvent) => void) | null = null;

    /**
     * Cache for converting pixel coordinates to cell indices.
     * Invalidated on resize and scroll (see ResizeObserver + updated() lifecycle).
     * @internal
     */
    private readonly _areaCellPositionCache = new AreaCellPositionCache(this);

    /**
     * ResizeObserver for cache invalidation on element resize.
     * @internal
     */
    private _resizeObserver?: ResizeObserver;

    /**
     * Observable stream of cell positions derived from mousemove events.
     * Emits AreaCellPosition when the mouse moves to a different cell.
     * Uses distinctUntilChanged to prevent duplicate emissions for same cell.
     * Shared among multiple subscribers (hover enter + leave/pairwise).
     * Lazy initialization: Created only when consumer (hover or click) is active.
     */
    public readonly areaCellPosition$!: Observable<AreaCellPosition>;

    override connectedCallback(): void {
        // Lazy initialization: Observable created BEFORE super.connectedCallback()
        // because super.connectedCallback() triggers hostConnected() on controllers,
        // which need the observable to be ready.
        // Opt-in check: Only create stream if enableCellHoverEvent is set
        if (this.areaConfiguration?.enableCellHoverEvent) {
            // @ts-expect-error - areaCellPosition$ is readonly, initialized here once
            this.areaCellPosition$ = this.setupAreaCellPositionStream();
        }

        super.connectedCallback();
        this.boundClickHandler = this.handleClick.bind(this);
        this.addEventListener('click', this.boundClickHandler);

        // Setup ResizeObserver for cache invalidation on element resize
        this._resizeObserver = new ResizeObserver(() => {
            this._areaCellPositionCache.invalidate();
        });
        this._resizeObserver.observe(this);
    }

    override disconnectedCallback(): void {
        super.disconnectedCallback();
        if (this.boundClickHandler) {
            this.removeEventListener('click', this.boundClickHandler);
            this.boundClickHandler = null;
        }

        // Cleanup ResizeObserver
        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
            this._resizeObserver = undefined;
        }
    }

    protected override updated(changedProperties: PropertyValues): void {
        super.updated(changedProperties);
        if (changedProperties.has('areaConfiguration')) {
            this.syncHostClasses();
        }

        // Invalidate cache when scroll position changes (areaCursorIndexX/Y)
        // ScrollController on O2dLayoutElement propagates scroll changes as properties to Area
        if (changedProperties.has('areaCursorIndexX') || changedProperties.has('areaCursorIndexY')) {
            this._areaCellPositionCache.invalidate();
        }
    }

    /**
     * Syncs CSS classes from area configuration to the host element.
     * Removes previously applied classes and adds current ones.
     */
    private syncHostClasses(): void {
        if (this.appliedClasses.length) {
            this.classList.remove(...this.appliedClasses);
        }
        const current = this.areaConfiguration?.classes ?? [];
        if (current.length) {
            this.classList.add(...current);
        }
        this.appliedClasses = current;
    }

    protected override shouldUpdate(changedProperties: PropertyValues): boolean {
        return !!this.areaConfiguration
            && !isNaN(this.areaSpanX)
            && !isNaN(this.areaSpanY)
            && !isNaN(this.areaCursorIndexX)
            && !isNaN(this.areaCursorIndexY)
            && !!this.dataProvider
            && super.shouldUpdate(changedProperties);
    }

    /**
     * Renders the content asynchronously, batched to the next animation frame.
     * Cancels pending renders when a newer viewport arrives before the next frame,
     * ensuring only the latest state is rendered.
     *
     * @return {unknown} The rendered output or a placeholder until the asynchronous rendering completes.
     */
    protected override render(): unknown {
        return [
            this.renderScopedStyles(),
            until(this.scheduleRenderAsync(this.areaConfiguration))
        ];
    }

    /**
     * Renders scoped CSS styles for this area using CSS @scope.
     * The implicit @scope (inside a <style> element) automatically
     * scopes to the parent element (this o2d-area).
     * Uses guard directive to prevent unnecessary style re-parsing.
     */
    private renderScopedStyles(): unknown {
        return guard([this.areaConfiguration?.styles], () =>
            this.areaConfiguration?.styles
                ? html`<style>@scope { ${this.areaConfiguration.styles.cssText} }</style>`
                : nothing
        );
    }

    /**
     * Delegates to renderAsync directly. No animation frame scheduling needed
     * because getViewportItemsForViewportAsync resolves synchronously from cache
     * during scroll (< 5ms), avoiding the extra frame delay that caused scroll jumps.
     */
    private scheduleRenderAsync(config: AreaConfiguration<TXValue, TYValue>): Promise<unknown> {
        return this.renderAsync(config);
    }


    /**
     * Asynchronously renders a specific area of data based on the provided configuration.
     *
     * @param {AreaConfiguration<TXValue, TYValue>} areaConfiguration - The configuration object containing rendering details and grid cell renderer.
     * @return {Promise<unknown>} A promise that resolves to the rendered content.
     */
    private async renderAsync(areaConfiguration: AreaConfiguration<TXValue, TYValue>): Promise<unknown> {

        // Load ViewportItems
        const viewport: Viewport = {
            indexRangeX: [this.areaCursorIndexX, this.areaCursorIndexX + this.areaSpanX - 1],
            indexRangeY: [this.areaCursorIndexY, this.areaCursorIndexY + this.areaSpanY - 1]
        };

        let viewportItems: ViewportItem<unknown>[] = await this.dataProvider.getViewportItemsForViewportAsync(viewport);
        if (areaConfiguration.enableClustering === true) {
            viewportItems = (new ClusterDetector()).detect(viewportItems);
        }

        // Render items with CSS Grid positioning
        return [
            areaConfiguration.columnUnderlayRenderer
                ? this.renderColumnUnderlay(areaConfiguration.columnUnderlayRenderer)
                : nothing,
            areaConfiguration.rowUnderlayRenderer
                ? this.renderRowUnderlay(areaConfiguration.rowUnderlayRenderer)
                : nothing,
            areaConfiguration.showBackgroundGrid === true
                ? this.renderBackgroundGrid()
                : nothing,
            repeat(
                viewportItems,
                (item: ViewportItem<unknown>) => item.id,
                (item: ViewportItem<unknown>) => {
                    const viewItemStyles: Readonly<StyleInfo> = {
                        gridArea: `${item.visibleIndexRangeY[0] - this.areaCursorIndexY + 1} / ${item.visibleIndexRangeX[0] - this.areaCursorIndexX + 1} / ${item.visibleIndexRangeY[1] - this.areaCursorIndexY + 2} / ${item.visibleIndexRangeX[1] - this.areaCursorIndexX + 2}`
                    }
                    return Array.isArray(item.data)
                        ? this.renderCluster(areaConfiguration, item.data, viewItemStyles)
                        : this.renderSingle(areaConfiguration, item, viewItemStyles);
                }
            )
        ];
    }

    /**
     * Renders a cluster of items within a specific grid area on the viewport.
     *
     * @param {AreaConfiguration<TXValue, TYValue>} areaConfiguration - Configuration for the area, including dimensions and display rules.
     * @param {ViewportItem<unknown>[]} memberItems - Array of items to be rendered in the cluster, each containing position and visibility details.
     * @param {Readonly<StyleInfo>} clusterGridArea - Style information defining the grid area where the cluster will be rendered.
     * @return {unknown} The rendered cluster container with its items structured in the specified grid layout.
     */
    private renderCluster(areaConfiguration: AreaConfiguration<TXValue, TYValue>, memberItems: ViewportItem<unknown>[], clusterGridArea: Readonly<StyleInfo>): unknown {
        const maxZ = Math.max(...memberItems.map(item => item.z));

        const clusterContainerStyles: Readonly<StyleInfo> = {
            ...clusterGridArea,
            display: 'grid',
            gridTemplateColumns: 'subgrid',
            gridTemplateRows: `repeat(${maxZ + 1}, 1fr)`,
        };

        return html`
            <div style=${styleMap(clusterContainerStyles)}>
                ${repeat(
                    memberItems,
                    (item) => item.id,
                    (item) => {
                        const itemStyles: Readonly<StyleInfo> = {
                            gridArea: `${item.z + 1} / ${item.visibleIndexRangeX[0] + 1} / ${item.z + 2} / ${item.visibleIndexRangeX[1] + 2}`
                        };
                        return this.renderSingle(areaConfiguration, item, itemStyles);
                    }
                )}
            </div>
        `;
    }

    /**
     * Renders a single viewport item based on the provided area configuration and styles.
     * The wrapper div includes a data-item-id attribute for DOM hit-testing in click handlers.
     *
     * @param areaConfiguration The configuration object that defines how the area and its cells should be rendered.
     * @param item The viewport item to be rendered. The item.id is used for data-item-id attribute.
     * @param viewItemStyles The read-only style information applied to the rendered item.
     * @return The rendered item as a templated result.
     */
    private renderSingle(areaConfiguration: AreaConfiguration<TXValue, TYValue>, item: ViewportItem<unknown>, viewItemStyles: Readonly<StyleInfo>): unknown {
        return html`<div data-item-id="${item.id}" style=${styleMap(viewItemStyles)}>${areaConfiguration.cellRenderer(item, this.dataProvider)}</div>`;
    }

    /**
     * Renders a column underlay layer: one full-height div per visible column where
     * the renderer returns a non-null UnderlayStyle. The container uses CSS subgrid
     * so each cell div aligns with the area's column tracks.
     * pointer-events: none ensures clicks pass through to items below.
     *
     * @param renderer - Callback that returns UnderlayStyle or null for each column
     */
    private renderColumnUnderlay(renderer: ColumnUnderlayRenderer<TXValue>): unknown {
        const containerStyles: Readonly<StyleInfo> = {
            gridArea: '1 / 1 / -1 / -1',
            display: 'grid',
            gridTemplateColumns: 'subgrid',
            gridTemplateRows: 'subgrid',
            pointerEvents: 'none',
            zIndex: 0,
        };
        return html`
            <div class="o2d-area-column-underlay" style=${styleMap(containerStyles)}>
                ${map(range(this.areaSpanX), (col) => {
                    const absX = this.areaCursorIndexX + col;
                    const xValue = this.dataProvider.axisX.getValue(absX);
                    if (xValue === undefined) return nothing;
                    const result = renderer(absX, xValue);
                    if (!result) return nothing;
                    const classInfo = {
                        'o2d-area-column-underlay-cell': true,
                        ...result.classes,
                    };
                    const cellStyles: Readonly<StyleInfo> = {
                        gridArea: `1 / ${col + 1} / -1 / ${col + 2}`,
                    };
                    return html`<div class=${classMap(classInfo)} style=${styleMap(cellStyles)}></div>`;
                })}
            </div>`;
    }

    /**
     * Renders a row underlay layer: one full-width div per visible row where
     * the renderer returns a non-null UnderlayStyle. The container uses CSS subgrid
     * so each cell div aligns with the area's row tracks.
     * pointer-events: none ensures clicks pass through to items below.
     *
     * @param renderer - Callback that returns UnderlayStyle or null for each row
     */
    private renderRowUnderlay(renderer: RowUnderlayRenderer<TYValue>): unknown {
        const containerStyles: Readonly<StyleInfo> = {
            gridArea: '1 / 1 / -1 / -1',
            display: 'grid',
            gridTemplateColumns: 'subgrid',
            gridTemplateRows: 'subgrid',
            pointerEvents: 'none',
            zIndex: 0,
        };
        return html`
            <div class="o2d-area-row-underlay" style=${styleMap(containerStyles)}>
                ${map(range(this.areaSpanY), (row) => {
                    const absY = this.areaCursorIndexY + row;
                    const yValue = this.dataProvider.axisY.getValue(absY);
                    if (yValue === undefined) return nothing;
                    const result = renderer(absY, yValue);
                    if (!result) return nothing;
                    const classInfo = {
                        'o2d-area-row-underlay-cell': true,
                        ...result.classes,
                    };
                    const cellStyles: Readonly<StyleInfo> = {
                        gridArea: `${row + 1} / 1 / ${row + 2} / -1`,
                    };
                    return html`<div class=${classMap(classInfo)} style=${styleMap(cellStyles)}></div>`;
                })}
            </div>`;
    }

    /**
     * Renders a background grid layer with explicitly positioned divs for each cell,
     * enabling CSS border styling on individual grid cells.
     * Uses CSS subgrid to align with the area's column/row tracks.
     */
    private renderBackgroundGrid(): unknown {
        const cellCount = this.areaSpanX * this.areaSpanY;

        return html`
            <div class="o2d-area-background-grid"
                 style="grid-area: 1 / 1 / -1 / -1; display: grid; grid-template-columns: subgrid; grid-template-rows: subgrid;">
                ${map(range(cellCount), (i) => {
                    const row = Math.floor(i / this.areaSpanX) + 1;
                    const col = (i % this.areaSpanX) + 1;
                    return html`<div style="grid-area: ${row} / ${col} / ${row + 1} / ${col + 1}"></div>`;
                })}
            </div>
        `;
    }

    /**
     * Handles click events on the area element.
     * Converts pixel coordinates to cell indices and dispatches cell-clicked event.
     * Performs DOM hit-test to identify the specific clicked item.
     */
    private handleClick(event: MouseEvent): void {
        // Only handle left-clicks (button 0)
        if (event.button !== 0) {
            return;
        }

        // Perform DOM hit-test to find target item ID
        const targetItemId = findTargetItemId(event);

        this.dispatchCellClickedEventForCell(
            event.clientX,
            event.clientY,
            event.button,
            {
                ctrlKey: event.ctrlKey,
                shiftKey: event.shiftKey,
                altKey: event.altKey,
                metaKey: event.metaKey,
            },
            targetItemId
        );
    }

    /**
     * Builds a CellEventDetail payload for the cell at given client coordinates.
     * Shared logic between cell-clicked and cell-hover events (DRY).
     *
     * This function:
     * 1. Converts pixel coordinates to cell indices via AreaCellPositionCache
     * 2. Fetches items at the crosspoint via DataProvider
     * 3. Resolves target item via hit-test if targetItemId provided
     *
     * @param clientX Absolute X coordinate in viewport (from MouseEvent.clientX)
     * @param clientY Absolute Y coordinate in viewport (from MouseEvent.clientY)
     * @param targetItemId Optional item ID from DOM hit-test (via findTargetItemId)
     * @returns CellEventDetail payload or null if coordinates are out of bounds
     */
    public async buildCellEventPayload(
        clientX: number,
        clientY: number,
        targetItemId: string | null = null
    ): Promise<CellEventDetail<TXValue, TYValue, unknown> | null> {
        // Convert pixel coordinates to cell indices via AreaCellPositionCache
        const { areaCellX, areaCellY } = this._areaCellPositionCache.resolveAreaCell(
            clientX,
            clientY
        );

        if (areaCellX === null || areaCellY === null) {
            // Out of bounds or unparsable grid template
            return null;
        }

        // Calculate absolute axis indices
        const absoluteIndexX = this.areaCursorIndexX + areaCellX;
        const absoluteIndexY = this.areaCursorIndexY + areaCellY;

        // Get axis values
        const axisXValue = this.dataProvider.axisX.getValue(absoluteIndexX) as TXValue;
        const axisYValue = this.dataProvider.axisY.getValue(absoluteIndexY) as TYValue;

        // Load items at this crosspoint (1×1 viewport)
        const viewport: Viewport = {
            indexRangeX: [absoluteIndexX, absoluteIndexX],
            indexRangeY: [absoluteIndexY, absoluteIndexY]
        };

        const items: ViewportItem<unknown>[] = await this.dataProvider.getViewportItemsForViewportAsync(viewport);

        // Resolve target item via hit-test
        const targetItem = targetItemId ? items.find(item => item.id === targetItemId) : undefined;

        // Build payload
        return {
            area: this as unknown as O2dAreaElement,
            dataProviderKey: this.dataProvider.dataProviderKey,
            axisX: {
                index: absoluteIndexX,
                value: axisXValue,
            },
            axisY: {
                index: absoluteIndexY,
                value: axisYValue,
            },
            items,
            targetItem,
        };
    }

    /**
     * Dispatches a cell-clicked event for the given cell coordinates.
     * Uses buildCellEventPayload() for shared payload construction.
     *
     * Requires opt-in: Only fires event if areaConfiguration.enableCellClickEvent is true.
     * This prevents unwanted events on header areas, sidebar areas, etc.
     *
     * @param clientX Absolute X coordinate in viewport (from MouseEvent.clientX)
     * @param clientY Absolute Y coordinate in viewport (from MouseEvent.clientY)
     * @param button Mouse button (0=left, 1=middle, 2=right)
     * @param modifiers Keyboard modifier state
     * @param targetItemId Optional item ID from DOM hit-test (via findTargetItemId)
     */
    private async dispatchCellClickedEventForCell(
        clientX: number,
        clientY: number,
        button: number,
        modifiers: { ctrlKey: boolean; shiftKey: boolean; altKey: boolean; metaKey: boolean },
        targetItemId: string | null = null
    ): Promise<void> {
        // Opt-in guard: Only dispatch events on areas with enableCellClickEvent flag
        if (!this.areaConfiguration?.enableCellClickEvent) {
            return;
        }

        // Build payload via shared function
        const basePayload = await this.buildCellEventPayload(clientX, clientY, targetItemId);
        if (!basePayload) {
            // Out of bounds
            return;
        }

        // Add click-specific fields
        const detail: CellClickedEventDetail<TXValue, TYValue, unknown> = {
            ...basePayload,
            button,
            modifiers,
        };

        this.fireEvent('cell-clicked', detail);
    }

    /**
     * Handles right-click without drag (called by ScrollController).
     * Dispatches cell-clicked event with button=2.
     * Performs DOM hit-test to identify the specific clicked item.
     *
     * @param clientX Absolute X coordinate in viewport
     * @param clientY Absolute Y coordinate in viewport
     * @param modifiers Keyboard modifier state
     * @param targetItemId Optional item ID from DOM hit-test (via findTargetItemId)
     */
    public async handleRightClickWithoutDrag(
        clientX: number,
        clientY: number,
        modifiers: { ctrlKey: boolean; shiftKey: boolean; altKey: boolean; metaKey: boolean },
        targetItemId: string | null = null
    ): Promise<void> {
        return this.dispatchCellClickedEventForCell(clientX, clientY, 2, modifiers, targetItemId);
    }

    /**
     * Sets up an RxJS Observable stream that emits AreaCellPosition on mousemove events.
     * The stream:
     * 1. Listens to mousemove events on this element
     * 2. Maps pixel coordinates to cell indices via AreaCellPositionCache
     * 3. Filters out-of-bounds coordinates (null cells)
     * 4. Deduplicates emissions using distinctUntilChanged (only emit on cell change)
     * 5. Shares the stream among multiple subscribers (hover enter + leave/pairwise)
     *
     * @returns Observable<AreaCellPosition> that emits when mouse moves to a different cell
     */
    private setupAreaCellPositionStream(): Observable<AreaCellPosition> {
        // A tap makes the browser emulate mousemove/mousedown/mouseup/click. Those emulated
        // moves must not count as hover: on touch nothing ever "leaves", so the hover preview a
        // consumer opens would stay up after every tap (review 0042, 2026-10-17).
        const lastTouchAt$ = merge(fromEvent(this, 'touchstart'), fromEvent(this, 'touchend')).pipe(
            rxMap(() => performance.now()),
            startWith(Number.NEGATIVE_INFINITY)
        );
        return fromEvent<MouseEvent>(this, 'mousemove').pipe(
            withLatestFrom(lastTouchAt$),
            filter(([event, lastTouchAt]) => !isEmulatedFromTouch(event, lastTouchAt)),
            rxMap(([event]) => event),
            rxMap(event => {
                const { areaCellX, areaCellY } = this._areaCellPositionCache.resolveAreaCell(
                    event.clientX,
                    event.clientY
                );

                if (areaCellX === null || areaCellY === null) {
                    return null; // Out of bounds
                }

                const absoluteIndexX = this.areaCursorIndexX + areaCellX;
                const absoluteIndexY = this.areaCursorIndexY + areaCellY;

                // Resolve targetItemId via DOM hit-test for within-cell item tracking
                let targetItemId: string | null = null;
                const elementAtCursor = document.elementFromPoint(event.clientX, event.clientY);
                if (elementAtCursor instanceof HTMLElement) {
                    targetItemId = elementAtCursor.closest('[data-item-id]')?.getAttribute('data-item-id') ?? null;
                }

                return {
                    areaCellX,
                    areaCellY,
                    absoluteIndexX,
                    absoluteIndexY,
                    clientX: event.clientX,
                    clientY: event.clientY,
                    targetItemId,
                };
            }),
            filter((pos): pos is AreaCellPosition => pos !== null),
            distinctUntilChanged((prev, curr) =>
                prev.absoluteIndexX === curr.absoluteIndexX &&
                prev.absoluteIndexY === curr.absoluteIndexY &&
                prev.targetItemId === curr.targetItemId
            ),
            // share() sufficient — no late subscribers need replay
            // share() auto-unsubscribes from mousemove when all subscribers disconnect
            share() // Multiple subscribers (enter + leave/pairwise) share one mousemove listener
        );
    }
}